import axios from 'axios';
import { installAxiosTiming } from './apiTiming';
import { Person, FilterOptions, SearchParams } from '../types';
import type {
  CandidateSourceLayersResponse,
  CertificationInput,
  DomainInput,
  EducationInput,
  ExperienceEntryInput,
  LanguageInput,
  SkillInput,
} from '../types/candidateDetails';
import { useAuth } from '../hooks/useAuth';

/**
 * Phase 3E-1 — request shape for the overview edit drawer. Mirrors
 * `OverviewFieldsPatch` on the backend. Only supply the keys the user
 * actually edited; omitted keys are left untouched by the server.
 */
export interface OverviewFieldsPatch {
  title?: string | null;
  name?: string | null;
  contact?: string | null;
  linkedin_profile_url?: string | null;
  primary_location?: string | null;
  professional_summary?: string | null;
  seniority_level?: 'intern' | 'junior' | 'mid' | 'senior' | 'lead' | 'principal' | null;
  management_level?: string | null;
  education_level?: string | null;
  availability_status?: 'available' | 'open' | 'not_looking' | null;
  /** YYYY-MM-DD. Admin-only on the server; non-admins get 403. */
  availability_date?: string | null;
  notice_period_weeks?: number | null;
  location_model_preferences?: ReadonlyArray<'onsite' | 'hybrid' | 'remote'>;
  employment_type_preferences?: ReadonlyArray<
    'fulltime' | 'contract' | 'freelance' | 'internship' | 'other'
  >;
  years_experience_total?: number | null;
  professional_interests?: string | null;
  job_responsibilities?: string | null;
}

const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
/** Backend root including `/api` (same rules as chatApi) — use for raw `fetch` to `/api/...` paths. */
export const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

installAxiosTiming(api, 'api');

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const authData = localStorage.getItem('cd-people-auth');
  if (authData) {
    try {
      const { state } = JSON.parse(authData);
      if (state.token) {
        config.headers.Authorization = `Bearer ${state.token}`;
      }
    } catch (error) {
      console.error('Failed to parse auth data:', error);
    }
  }
  return config;
});

// Handle 401 responses - trigger logout to update Zustand state
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check for skipLogout flag in config (added via custom property)
    const skipLogout = error.config?.skipLogout === true;

    if (error.response?.status === 401 && !skipLogout) {
      // Call logout from Zustand store directly - this updates the state
      // and clears localStorage, causing app to show login screen
      useAuth.getState().logout();
    }
    return Promise.reject(error);
  }
);

export const peopleApi = {
  // Get all people
  async getAll(): Promise<Person[]> {
    const { data } = await api.get('/people');
    return data.people;
  },

  // Search people
  async search(params: SearchParams): Promise<Person[]> {
    const { data } = await api.get('/people/search', { params });
    return data.people;
  },

  // Get specific person
  async getById(id: number): Promise<Person> {
    const { data } = await api.get(`/people/${id}`);
    return data.person;
  },

  // Update person (e.g. contact) – admin only
  async updatePerson(
    id: number,
    payload: { contact?: string; linkedin_profile_url?: string | null }
  ): Promise<Person> {
    const { data } = await api.patch(`/people/${id}`, payload);
    return data as Person;
  },

  /**
   * Phase 3F — flip review_status on an external_candidate.
   * Permission is enforced server-side (pool owner/editor of any pool that
   * contains the person, or admin). Returns the updated Person with
   * `CandidateDetails.reviewStatus/reviewedAt/reviewedBy` refreshed.
   */
  async patchReviewStatus(
    id: number,
    payload: {
      review_status: 'unreviewed' | 'needs_review' | 'reviewed';
      review_notes?: string | null;
    }
  ): Promise<Person> {
    const { data } = await api.patch(`/people/${id}`, payload);
    return data as Person;
  },

  /**
   * Phase 3E-1 — partial update of the overview-editable fields on an
   * external candidate. Backend validates enums strictly and stamps
   * `projection_locks` so the next CV re-import leaves manual values alone.
   * Throws if any field fails validation (route maps to 400).
   *
   * Only pass keys the user actually edited; omitted keys are untouched.
   */
  async patchOverviewFields(
    id: number,
    payload: OverviewFieldsPatch
  ): Promise<Person> {
    const { data } = await api.patch(`/people/${id}`, payload);
    return data as Person;
  },

  /**
   * Phase 3E-2 — atomically replace the `admin_edit` shadow layer of the
   * candidate's experience collection (entries + nested projects).
   *
   * The backend DELETEs every existing `admin_edit` row and INSERTs the
   * submitted ones in a single transaction, then stamps
   * `projection_locks.experience = true` so the next CV re-import leaves
   * the manual edits alone. The `cv` layer is untouched and remains
   * available via `getSourceLayers()`.
   *
   * 4xx responses carry `{ error, field?, allowed?, limit? }` with a
   * dotted field path — callers should surface these inline on the form.
   */
  async replaceExperience(
    id: number,
    entries: ExperienceEntryInput[]
  ): Promise<Person> {
    const { data } = await api.put(`/people/${id}/experience`, { entries });
    return data as Person;
  },

  /**
   * Phase 3E-2 — load the per-layer shadow snapshots of the candidate's
   * evidence collections (experiences, education, certifications) plus the
   * raw CV text. Used by the Experience tab's view selector to compare
   * Current / CV parse / CV text / Candidate input side by side without
   * re-running projection precedence on every switch.
   *
   * ACL mirrors the PUT endpoint (admin or pool reviewer); 403/404 throw.
   */
  async getSourceLayers(id: number): Promise<CandidateSourceLayersResponse> {
    const { data } = await api.get(`/people/${id}/source-layers`);
    return data as CandidateSourceLayersResponse;
  },

  /**
   * Phase 3E-3 — atomically replace the `admin_edit` shadow layer of the
   * candidate's skills (`person_skills.source = 'admin_edit'`). The `cv`
   * layer is left intact.
   */
  async replaceSkills(id: number, skills: SkillInput[]): Promise<Person> {
    const { data } = await api.put(`/people/${id}/skills`, { skills });
    return data as Person;
  },

  /**
   * Phase 3E-3 — full-wipe replace of `person_domains`. The
   * UNIQUE(person_id, domain) constraint precludes shadow layering, so
   * every edit replaces the full set; `projection_locks.domains` is
   * still stamped so CV re-import won't overwrite.
   */
  async replaceDomains(id: number, domains: DomainInput[]): Promise<Person> {
    const { data } = await api.put(`/people/${id}/domains`, { domains });
    return data as Person;
  },

  /**
   * Full-wipe replace of `person_languages` (UNIQUE per language; same model
   * as domains). Body: `{ languages: LanguageInput[] }`.
   */
  async replaceLanguages(id: number, languages: LanguageInput[]): Promise<Person> {
    const { data } = await api.put(`/people/${id}/languages`, { languages });
    return data as Person;
  },

  /** Phase 3E-3 — atomic PUT for education (shadow-layered). */
  async replaceEducation(id: number, entries: EducationInput[]): Promise<Person> {
    const { data } = await api.put(`/people/${id}/education`, { entries });
    return data as Person;
  },

  /** Phase 3E-3 — atomic PUT for certifications (shadow-layered). */
  async replaceCertifications(
    id: number,
    entries: CertificationInput[]
  ): Promise<Person> {
    const { data } = await api.put(`/people/${id}/certifications`, { entries });
    return data as Person;
  },

  async archivePerson(
    id: number,
    reason?: string
  ): Promise<{ ok: boolean; archived: boolean; person_id: number }> {
    const { data } = await api.post(`/people/${id}/archive`, { reason: reason?.trim() || undefined });
    return data;
  },

  /** Admin only — restores visibility; returns updated person. */
  async unarchivePerson(
    id: number
  ): Promise<{ ok: boolean; unarchived: boolean; person_id: number; person: Person }> {
    const { data } = await api.post(`/people/${id}/unarchive`);
    return data;
  },

  // Get filter options
  async getFilters(): Promise<FilterOptions> {
    const { data } = await api.get('/filters');
    return data;
  },

  // Get stats
  async getStats() {
    const { data } = await api.get('/stats');
    return data;
  },

  // Health check
  async health() {
    const { data } = await api.get('/health');
    return data;
  },
};

/** Admin: create a profile-update magic link for an employee/candidate. Requires admin auth. */
export interface ProfileUpdateLinkResult {
  url: string;
  token: string;
  expires_at: string;
}

export interface SendProfileUpdateEmailResult {
  success: boolean;
  email_sent: boolean;
  email_error?: string;
}

export type DirectoryEmailStatus = 'found' | 'not_found' | 'ambiguous';

export interface DirectoryEmailValidationRow {
  email: string;
  status: DirectoryEmailStatus;
  matchedPrimaryEmail?: string;
  matchedDisplayName?: string;
  detail?: string;
}

/**
 * Phase 3E-3 — catalog picker API for the Skills editor. Read path
 * (`getOntology`) is available to any authenticated user; `create` is
 * admin-only on the server (403 otherwise) and is used by the inline
 * "Create new skill" affordance in SkillsEditorModal.
 */
export interface SkillCatalogEntry {
  id: string;
  name: string;
}

export interface CreateSkillResult {
  id: string;
  name: string;
  existed: boolean;
}

export const skillsApi = {
  /**
   * Flat catalog for typeahead. Loaded once per modal open and filtered
   * client-side. The server response includes every canonical skill id
   * from the ontology + graph + any user-created rows.
   */
  async getOntology(): Promise<SkillCatalogEntry[]> {
    const { data } = await api.get<{ skills: SkillCatalogEntry[] }>(
      '/skills/ontology'
    );
    return data.skills ?? [];
  },

  /**
   * Admin-only. Mint a new catalog row or return the existing row when
   * `normalized_name` already matches. `existed=true` means no write,
   * the caller can treat the returned `{id,name}` as if it had always
   * been in the catalog.
   */
  async create(name: string, category?: string | null): Promise<CreateSkillResult> {
    const body: Record<string, string> = { name };
    if (category) body.category = category;
    const { data } = await api.post<CreateSkillResult>('/skills', body);
    return data;
  },
};

export const directoryValidationApi = {
  async validateEmails(googleAccessToken: string, emails: string[]): Promise<{ results: DirectoryEmailValidationRow[] }> {
    const { data } = await api.post<{ results: DirectoryEmailValidationRow[] }>('/directory/validate-emails', {
      googleAccessToken,
      emails,
    });
    return data;
  },
};

export const adminApi = {
  async createProfileUpdateLink(personId: number, expiresInDays: number = 7): Promise<ProfileUpdateLinkResult> {
    const { data } = await api.post<ProfileUpdateLinkResult>(
      '/admin/profile-update-links',
      { person_id: personId, expires_in_days: expiresInDays }
    );
    return { url: data.url, token: data.token, expires_at: data.expires_at };
  },

  /** Send the profile-update link by email (non-blocking). Uses the token from createProfileUpdateLink. */
  async sendProfileUpdateLinkEmail(token: string): Promise<SendProfileUpdateEmailResult> {
    const { data } = await api.post<SendProfileUpdateEmailResult>('/admin/profile-update-links/send-email', { token });
    return data;
  },
};


